"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.PagefindSearchController = void 0;
const schedule_controller_1 = require("@/controllers/schedule-controller");
const DEBOUNCE_MS = 200;
const MAX_RESULTS = 8;
// Pagefind excerpt는 일치 구간을 <mark>로 감싼 HTML이라 태그를 걷어 설명 문구로 쓴다.
function toSearchResult(result) {
    return {
        href: result.url,
        label: result.meta.title || result.url,
        description: result.excerpt.replace(/<[^>]*>/g, ''),
    };
}
/**
 * Pagefind 색인 로딩·debounce·요청 순서 관리를 소유하는 검색 컨트롤러.
 * query·results·searching이 바뀌면 호스트를 다시 그린다.
 * 보여줄 결과가 아직 없으면 입력 직후부터 결과가 돌아올 때까지 searching이 켜져, debounce와 색인 로딩 중에
 * "결과 없음"이 잠깐 비치지 않는다. 보여줄 결과가 있으면 요청이 시작될 때까지 이전 결과를 그대로 둔다.
 */
class PagefindSearchController {
    constructor(host, options) {
        this.host = host;
        this.options = options;
        this.query = '';
        this.results = [];
        this.searching = false;
        this.pagefind = null;
        this.requestId = 0;
        this.debounce = new schedule_controller_1.ScheduleController(host, () => this.search(this.query), {
            delay: DEBOUNCE_MS,
        });
    }
    // 열릴 때의 미리 불러오기와 첫 검색이 겹쳐도 한 번만 불러온다. 실패하면 다음 호출이 다시 시도한다.
    async load() {
        if (this.pagefind)
            return;
        this.loading ??= this.importPagefind().finally(() => {
            this.loading = undefined;
        });
        await this.loading;
    }
    async importPagefind() {
        try {
            // webpack이 번들링하지 않도록 Function constructor로 동적 import한다.
            // tsconfig가 commonjs라 import()를 직접 쓰면 require로 바뀌어 webpackIgnore 주석이 소용없다.
            const dynamicImport = new Function('url', 'return import(url)');
            this.pagefind = (await dynamicImport('/pagefind/pagefind.js'));
        }
        catch {
            console.warn('Pagefind not available. Run npm run build first.');
        }
    }
    setQuery(query) {
        this.query = query;
        if (!query.trim()) {
            this.reset();
            return;
        }
        if (this.results.length === 0)
            this.searching = true;
        this.debounce.request();
        this.host.requestUpdate();
    }
    reset(clearQuery = false) {
        if (clearQuery)
            this.query = '';
        this.debounce.cancel();
        this.results = [];
        this.searching = false;
        this.requestId++;
        this.host.requestUpdate();
    }
    async search(query) {
        const requestId = ++this.requestId;
        const searchQuery = query.trim();
        if (!searchQuery)
            return;
        await this.load();
        if (!this.isCurrent(requestId, searchQuery))
            return;
        if (!this.pagefind) {
            this.searching = false;
            this.host.requestUpdate();
            return;
        }
        this.searching = true;
        this.host.requestUpdate();
        try {
            const { results } = await this.pagefind.search(searchQuery);
            const data = await Promise.all(results.slice(0, MAX_RESULTS).map(r => r.data()));
            if (!this.isCurrent(requestId, searchQuery))
                return;
            this.results = data.map(toSearchResult);
        }
        finally {
            if (this.isCurrent(requestId, searchQuery))
                this.searching = false;
            this.host.requestUpdate();
        }
    }
    isCurrent(requestId, query) {
        return this.options.isActive() && requestId === this.requestId && this.query.trim() === query;
    }
}
exports.PagefindSearchController = PagefindSearchController;
//# sourceMappingURL=data:application/json;base64,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